import { AppError } from "@nwga/contracts/errors";
import type { Id } from "@nwga/contracts/id";

/**
 * Authorization, as a subsystem rather than a habit.
 *
 * Two things decide what someone may do:
 *
 *  1. their **platform role** (`member`, `moderator`, `admin`), and
 *  2. their **role inside a particular business** (`owner`, `admin`, `member`).
 *
 * Both are answered here, in data, so a permission question is asked the same
 * way from a tRPC procedure, a background task, or a React component. Services
 * call `authorization.require(...)`; nothing anywhere else compares a role
 * string by hand.
 *
 * This module imports nothing but `@nwga/contracts`, which is what lets the web
 * client use the exact same rules to decide whether to render a button.
 */

export type PlatformRole = "member" | "moderator" | "admin";
export type BusinessRole = "owner" | "admin" | "member";

export const PERMISSIONS = [
	// Businesses
	"business.create",
	"business.update",
	"business.delete",
	"business.member.manage",
	/** Approve or suspend any business. Platform staff only. */
	"business.moderate",

	// Job listings
	"job.create",
	"job.update",
	"job.delete",
	"job.publish",
	/** Read a business's drafts and closed listings. */
	"job.read.private",
	/** Edit or remove any listing, regardless of ownership. */
	"job.moderate",

	// Platform
	/** See the admin console at all. The gate on every screen in it. */
	"admin.access",
	"audit.read",
	/** Enumerate accounts. Administration, not moderation. */
	"user.read",
	/**
	 * Grant and revoke platform roles.
	 *
	 * Admin only, and deliberately its own permission rather than being inferred
	 * from `admin.access`: this is the permission that hands out permissions, so
	 * it must never be reachable by a role that merely moderates content.
	 */
	"user.role.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type BusinessId = Id<"business">;
export type UserId = Id<"user">;

/** What a platform role grants everywhere, independent of any business. */
const PLATFORM_GRANTS: Record<PlatformRole, readonly Permission[]> = {
	member: ["business.create"],
	moderator: [
		"business.create",
		"business.moderate",
		"job.moderate",
		"job.read.private",
		"admin.access",
	],
	// Listed explicitly rather than granted by a wildcard: a new permission
	// should have to be considered, not silently inherited.
	admin: [...PERMISSIONS],
};

/** What a role inside a business grants, for that business only. */
const BUSINESS_GRANTS: Record<BusinessRole, readonly Permission[]> = {
	member: ["job.create", "job.update", "job.read.private"],
	admin: [
		"business.update",
		"business.member.manage",
		"job.create",
		"job.update",
		"job.delete",
		"job.publish",
		"job.read.private",
	],
	owner: [
		"business.update",
		"business.delete",
		"business.member.manage",
		"job.create",
		"job.update",
		"job.delete",
		"job.publish",
		"job.read.private",
	],
};

/**
 * The authenticated caller, with their memberships already resolved.
 *
 * Memberships are loaded once per request and carried on the context, so a
 * permission check is a map lookup rather than a query — which is what makes it
 * cheap enough to call freely.
 */
export interface Actor {
	readonly userId: UserId;
	readonly role: PlatformRole;
	readonly memberships: ReadonlyMap<BusinessId, BusinessRole>;
}

/** The business a permission is being asked about, when it is business-scoped. */
export interface PermissionScope {
	readonly businessId?: BusinessId | undefined;
}

export function createActor(
	userId: UserId,
	role: PlatformRole,
	memberships: Iterable<readonly [BusinessId, BusinessRole]> = [],
): Actor {
	return { userId, role, memberships: new Map(memberships) };
}

/** The role an actor holds in a business, if any. */
export function roleIn(
	actor: Actor,
	businessId: BusinessId,
): BusinessRole | undefined {
	return actor.memberships.get(businessId);
}

export function can(
	actor: Actor,
	permission: Permission,
	scope: PermissionScope = {},
): boolean {
	if (PLATFORM_GRANTS[actor.role].includes(permission)) return true;

	if (scope.businessId === undefined) return false;

	const businessRole = actor.memberships.get(scope.businessId);
	if (businessRole === undefined) return false;

	return BUSINESS_GRANTS[businessRole].includes(permission);
}

/**
 * Throws `AppError.forbidden` unless the actor holds the permission.
 *
 * The message deliberately does not distinguish "you are not a member of this
 * business" from "this business does not exist" — that difference is itself
 * information.
 */
export function requirePermission(
	actor: Actor,
	permission: Permission,
	scope: PermissionScope = {},
): void {
	if (can(actor, permission, scope)) return;

	throw AppError.forbidden("You do not have permission to do that.", {
		reason: "authorization.denied",
		details: { permission },
	});
}

/**
 * The object services depend on.
 *
 * Injecting this rather than importing the functions directly keeps a service
 * test able to substitute a permissive or restrictive policy.
 */
export interface Authorization {
	can: (
		actor: Actor,
		permission: Permission,
		scope?: PermissionScope,
	) => boolean;
	require: (
		actor: Actor,
		permission: Permission,
		scope?: PermissionScope,
	) => void;
}

export const authorization: Authorization = {
	can,
	require: requirePermission,
};

/** Every permission an actor holds. Sent to the client to drive UI affordances. */
export function permissionsFor(
	actor: Actor,
	scope: PermissionScope = {},
): Permission[] {
	return PERMISSIONS.filter((permission) => can(actor, permission, scope));
}
